import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { notifyApiError } from "../../lib/toast-error";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useActiveAreas } from "../master-data/api";
import { useCreateOperationsLogEntry, type OperationsLogType } from "./api";
import {
  buildCreateRequest,
  EMPTY_DRAFT,
  OPERATIONS_LOG_TYPE_LABEL_KEY,
  OPERATIONS_LOG_TYPES,
  toLocalDateTimeValue,
  type EntryDraft,
  type EntryDraftErrors,
} from "./entry";

/**
 * Record a Betriebslog entry ([F-015], [US-014], [AC-016]): Typ, Zeitpunkt
 * (oder Zeitraum), betroffener Bereich and Kurzbeschreibung; the recording
 * person is the session user, set by the server. Offered only to whoever holds
 * `oplog.write` - the server decides again. After saving, the new entry opens.
 */
export function CreateEntryDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const areas = useActiveAreas().data ?? [];
  const create = useCreateOperationsLogEntry();
  const [draft, setDraft] = useState<EntryDraft>(() => ({
    ...EMPTY_DRAFT,
    startAt: toLocalDateTimeValue(new Date()),
  }));
  const [errors, setErrors] = useState<EntryDraftErrors>({});

  const update = (patch: Partial<EntryDraft>): void => setDraft((current) => ({ ...current, ...patch }));

  const close = (): void => {
    onOpenChange(false);
    setDraft({ ...EMPTY_DRAFT, startAt: toLocalDateTimeValue(new Date()) });
    setErrors({});
  };

  const submit = (): void => {
    const result = buildCreateRequest(draft);
    if (result.errors) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    create.mutate(result.request, {
      onSuccess: (entry) => {
        toast.success(t("operationsLog.dialog.created"));
        close();
        void navigate({ to: "/operations-log/$id", params: { id: entry.id } });
      },
      onError: (error) =>
        notifyApiError(error, {
          precondition: t("errors.precondition"),
          forbidden: t("errors.forbidden"),
          conflict: t("errors.conflict"),
          generic: t("errors.saveFailed"),
        }),
    });
  };

  const fieldError = (key: string | undefined, testid: string): JSX.Element | null =>
    key ? (
      <p data-testid={testid} className="text-sm text-destructive-text">
        {t(key)}
      </p>
    ) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent data-testid="operations-log-create_dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("operationsLog.dialog.title")}</DialogTitle>
          <DialogDescription>{t("operationsLog.dialog.intro")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("operationsLog.dialog.type")} *</Label>
            <Select value={draft.type} onValueChange={(value) => update({ type: value as OperationsLogType })}>
              <SelectTrigger data-testid="operations-log-create_type" aria-label={t("operationsLog.dialog.type")}>
                <SelectValue placeholder={t("operationsLog.dialog.typePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {OPERATIONS_LOG_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(OPERATIONS_LOG_TYPE_LABEL_KEY[type])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldError(errors.type, "operations-log-create_type-error")}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="operations-log-start">{t("operationsLog.dialog.start")} *</Label>
              <Input
                id="operations-log-start"
                data-testid="operations-log-create_start"
                type="datetime-local"
                value={draft.startAt}
                onChange={(event) => update({ startAt: event.target.value })}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="operations-log-create_start-now"
                onClick={() => update({ startAt: toLocalDateTimeValue(new Date()) })}
              >
                {t("operationsLog.dialog.now")}
              </Button>
              {fieldError(errors.startAt, "operations-log-create_start-error")}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="operations-log-end">{t("operationsLog.dialog.end")}</Label>
              <Input
                id="operations-log-end"
                data-testid="operations-log-create_end"
                type="datetime-local"
                value={draft.endAt}
                onChange={(event) => update({ endAt: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">{t("operationsLog.dialog.endHint")}</p>
              {fieldError(errors.endAt, "operations-log-create_end-error")}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("operationsLog.dialog.areas")} *</Label>
            <div
              role="group"
              aria-label={t("operationsLog.dialog.areas")}
              data-testid="operations-log-create_areas"
              className="flex flex-wrap gap-2"
            >
              {areas.map((area) => {
                const selected = draft.areaIds.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    data-testid={`operations-log-create_area-option_${area.id}`}
                    aria-pressed={selected}
                    onClick={() =>
                      update({
                        areaIds: selected
                          ? draft.areaIds.filter((id) => id !== area.id)
                          : [...draft.areaIds, area.id],
                      })
                    }
                    className={cn(
                      "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors md:min-h-9",
                      selected
                        ? "border-primary-text bg-primary text-primary-foreground"
                        : "border-input bg-background text-foreground hover:bg-accent",
                    )}
                  >
                    {selected ? <Check aria-hidden="true" className="h-4 w-4" /> : null}
                    {area.name}
                  </button>
                );
              })}
            </div>
            {fieldError(errors.areaIds, "operations-log-create_areas-error")}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="operations-log-description">{t("operationsLog.dialog.description")} *</Label>
            <Textarea
              id="operations-log-description"
              data-testid="operations-log-create_description"
              rows={3}
              value={draft.shortDescription}
              placeholder={t("operationsLog.dialog.descriptionPlaceholder")}
              onChange={(event) => update({ shortDescription: event.target.value })}
            />
            {fieldError(errors.shortDescription, "operations-log-create_description-error")}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="operations-log-create_cancel" onClick={close}>
            {t("common.cancel")}
          </Button>
          <Button data-testid="operations-log-create_submit" onClick={submit} disabled={create.isPending}>
            {create.isPending ? t("operationsLog.dialog.submitting") : t("operationsLog.dialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
